import axios from 'axios';
import { Endpoint } from 'types/url';
import { buildSearchParams } from 'utils/url';
import { SnakedGetParams } from '../api.schema';
import { BaseHttpClient } from '../BaseHttpClient';
import { injectCommonInterceptors } from '../interceptors';
import {
  GetUserResponse,
  CreateCustomerPortalSessionPayload,
  CreateCheckoutSessionPayload,
  CreateOrganizationTeamPayload,
  CreateOrganizationTeamResponse,
  SessionResponse,
  UpdateUserPayload,
  UpdateUserResponse,
  GetIndexingStatisticsResponse,
  GetIndexingConfigurationResponse,
  GetOrganizationAccessRequestsResponse,
  GetOrganizationResponse,
  GetOrganizationMembersResponse,
  GetOrganizationEnvMembersResponse,
  GetTeamUsersResponse,
  SSOSetupPayload,
  SSOSetupResponse,
  GetTeamsResponse,
  TeamData,
  UpdateOrganizationTeamPayload,
  UpdateOrganizationTeamResponse,
  GetPlansResponse,
  IsFeatureEnabledResponse,
} from './api.schema';
import { BASE_URL } from './consts';

const axiosPlatformInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Access-Control-Allow-Origin': '*',
  },
});

injectCommonInterceptors(axiosPlatformInstance);

export class PlatformClient extends BaseHttpClient {
  constructor() {
    super(axiosPlatformInstance, {
      defaultExtractNestedData: true,
    });
  }

  // Singleton instance for access without instantiation
  static override instance = new PlatformClient();

  /*****************
   *      GET
   *****************/
  static async getUser() {
    return await PlatformClient.get<GetUserResponse>('/users');
  }

  static async getOrganizationAccessRequests(): Promise<GetOrganizationAccessRequestsResponse> {
    return await PlatformClient.get('/organizations/requests');
  }

  static async getIndexingStatistics() {
    return await PlatformClient.get<GetIndexingStatisticsResponse>(
      '/indexer/statistics',
    );
  }

  static async getIndexingConfiguration() {
    return await PlatformClient.get<GetIndexingConfigurationResponse>(
      '/indexer/configuration',
    );
  }

  static async getPlans() {
    return await PlatformClient.get<GetPlansResponse>('/teams/plans');
  }

  static async getOrganization() {
    return await PlatformClient.get<GetOrganizationResponse>('/organizations');
  }

  static async getOrganizationMembers(params?: SnakedGetParams) {
    const searchParams = buildSearchParams({
      page_size: params?.page_size,
      offset: params?.offset,
      search: params?.search,
      sorting: params?.sorting,
      team_id: params?.team_id,
    });

    return await PlatformClient.get<GetOrganizationMembersResponse>(
      '/organizations/flat-members' as Endpoint,
      {
        extractNestedData: false,
        params: searchParams,
      },
    );
  }

  static async getOrganizationEnvMembers() {
    return await PlatformClient.get<GetOrganizationEnvMembersResponse>(
      '/organizations/members-by-envs' as Endpoint,
      {
        extractNestedData: false,
      },
    );
  }

  static async getTeamUsers(teamId: string) {
    const endpoint = `/teams/${teamId}/users`;

    return await PlatformClient.get<GetTeamUsersResponse>(
      endpoint as Endpoint,
      {
        extractNestedData: true,
      },
    );
  }

  static async getWorkspaceMembers(
    workspaceId: string | null,
    params?: SnakedGetParams,
  ) {
    const searchParams = buildSearchParams({
      page_size: params?.page_size,
      offset: params?.offset,
      search: params?.search,
      sorting: params?.sorting,
      team_id: params?.team_id,
    });

    const endpoint = `/accounts/workspaces/${workspaceId}/members`;

    return await PlatformClient.get<GetOrganizationMembersResponse>(
      endpoint as Endpoint,
      {
        extractNestedData: false,
        params: searchParams,
      },
    );
  }

  static async exportOrganizationMembers(): Promise<Blob> {
    return await PlatformClient.get<Blob>(
      '/organizations/flat-members/export',
      {
        responseType: 'blob',
        extractNestedData: false,
      },
    );
  }

  static async getOrganizationTeams() {
    return await PlatformClient.get<GetTeamsResponse>('/organizations/teams');
  }

  static async getUserTeams() {
    return await PlatformClient.get<GetTeamsResponse>('/teams');
  }

  static async isFeatureEnabled(featureId: string) {
    const searchParams = buildSearchParams({
      feature_id: featureId,
    });

    return await PlatformClient.get<IsFeatureEnabledResponse>(
      '/features/is_feature_enabled' as Endpoint,
      { params: searchParams, extractNestedData: false },
    );
  }

  static async getTeam(teamId: string) {
    return await PlatformClient.get(`/teams/${teamId}`);
  }

  static async getInvites() {
    return await PlatformClient.get('/invites?used=false');
  }

  /*****************
   *      POST
   *****************/
  static async inviteMembersToTeam(payload: FormData): Promise<void> {
    const emails = payload.get('emails');
    const team_id = payload.get('teamId');
    const file = payload.get('file');

    return await PlatformClient.post(
      '/invites',
      {
        team_id,
        ...(emails ? { emails } : {}),
        ...(file ? { file } : {}),
      },
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );
  }

  static async inviteMembersToOrganization(payload: FormData): Promise<void> {
    const emails = payload.get('emails');
    const file = payload.get('file');
    const team_id = payload.get('teamId');

    return await PlatformClient.post(
      '/invites/organization',
      {
        ...(team_id ? { team_id } : {}),
        ...(emails ? { emails } : {}),
        ...(file ? { file } : {}),
      },
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );
  }

  static async createCustomerPortalSession(
    payload: CreateCustomerPortalSessionPayload,
  ): Promise<SessionResponse> {
    return await PlatformClient.post(
      '/teams/create-customer-portal-session',
      payload,
    );
  }

  static async createCheckoutSession(
    payload: CreateCheckoutSessionPayload,
  ): Promise<SessionResponse> {
    return await PlatformClient.post('/teams/create-checkout-session', payload);
  }

  static async ssoSetup(payload: SSOSetupPayload): Promise<SSOSetupResponse> {
    return await PlatformClient.post('/organizations/sso-setup', payload);
  }

  static async createOrganizationTeam(
    payload: CreateOrganizationTeamPayload,
  ): Promise<CreateOrganizationTeamResponse> {
    const result = await PlatformClient.post<CreateOrganizationTeamResponse>(
      '/teams',
      payload,
    );

    if (!result) {
      throw new Error('Failed to create team');
    }

    return result;
  }

  static async updateOrganizationTeam(
    teamId: string,
    payload: UpdateOrganizationTeamPayload,
  ): Promise<UpdateOrganizationTeamResponse> {
    const result = await PlatformClient.patch<UpdateOrganizationTeamResponse>(
      `/teams/${teamId}`,
      payload,
    );

    if (!result) {
      throw new Error('Failed to update team');
    }

    return result;
  }

  /*****************
   *     PATCH
   *****************/
  static async disconnectGitProvider(provider: string): Promise<void> {
    return await PlatformClient.patch('/users/disconnect-git-provider', {
      git_provider: provider,
    });
  }

  static async updateUser(
    payload: UpdateUserPayload,
  ): Promise<UpdateUserResponse> {
    return await PlatformClient.patch('/users', payload);
  }

  static async updateOrganizationGitLinking(
    git_linking_enabled: boolean,
  ): Promise<void> {
    return await PlatformClient.patch('/organizations', {
      git_linking_enabled,
    });
  }

  static async updateOrganization(payload: Record<string, any>): Promise<void> {
    return await PlatformClient.patch('/organizations', payload);
  }

  static async respondToOrganizationAccessRequests(payload: {
    request_ids: string[];
    approved: boolean;
  }): Promise<void> {
    return await PlatformClient.patch('/organizations/requests', payload);
  }

  /*****************
   *     DELETE
   *****************/
  static async deleteMember(userId: string): Promise<void> {
    const encodedUserId = encodeURIComponent(userId);

    return await PlatformClient.delete(`/organizations/users/${encodedUserId}`);
  }

  static async deleteTeam(teamId: string): Promise<{ team: TeamData }> {
    const result = await PlatformClient.delete<{ team: TeamData }>(
      `/teams/${teamId}`,
    );

    if (!result) {
      throw new Error('Failed to delete team or team not found');
    }

    return result;
  }

  static async deleteSelf(): Promise<void> {
    return await PlatformClient.delete('/users');
  }

  static async deleteInvite(inviteId: string): Promise<void> {
    const encodedInviteId = encodeURIComponent(inviteId);

    return await PlatformClient.delete(
      `/organizations/users/${encodedInviteId}`,
    );
  }

  static async deleteTeamMember(teamId: string, userId: string): Promise<void> {
    const encodedUserId = encodeURIComponent(userId);

    return await PlatformClient.delete(
      `/teams/${teamId}/users/${encodedUserId}`,
    );
  }

  static async deleteTeamInvite(teamId: string, email: string): Promise<void> {
    const encodedEmail = encodeURIComponent(email);

    return await PlatformClient.delete(
      `/teams/${teamId}/invites/${encodedEmail}`,
    );
  }

  static async respondToInvite(
    inviteId: string,
    accept: boolean,
  ): Promise<void> {
    return await PlatformClient.patch(`/invites/${inviteId}`, { accept });
  }
}
